import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {api} from '../../api';

let editorCounter = 0;

function loadDocsAPI(serverUrl) {
  return new Promise((resolve, reject) => {
    if (window.DocsAPI) { resolve(); return; }
    const script = document.createElement('script');
    script.src = `${serverUrl}/web-apps/apps/api/documents/api.js`;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function PolicyDocumentEditor() {
  const {id} = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const hasUnsavedRef = useRef(false);
  const pendingNavRef = useRef(false);
  const editorIdRef = useRef(`onlyoffice-editor-${++editorCounter}`);

  const backPath = '/policies';

  const handleBeforeUnload = useCallback((e) => {
    if (hasUnsavedRef.current) { e.preventDefault(); e.returnValue = ''; }
  }, []);

  const handleBack = () => {
    if (!hasUnsavedRef.current || !editorRef.current) { navigate(backPath); return; }
    setSaving(true);
    pendingNavRef.current = true;
    try { editorRef.current.triggerForceSave(); } catch (_) {}
    setTimeout(() => { if (pendingNavRef.current) navigate(backPath); }, 5000);
  };

  useEffect(() => {
    const containerId = editorIdRef.current;
    const displayName = decodeURIComponent(document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('displayName='))?.split('=')?.[1] || '');

    api(`/policies/documents/${id}/editor-config?userName=${encodeURIComponent(displayName)}`).then(r => r.ok ? r.json() : null).then(data => {
      if (!data) { setError('Document not found'); setLoading(false); return; }
      const config = data.config;
      config.events = {
        onDocumentStateChange: (e) => {
          hasUnsavedRef.current = e.data;
          setSaveStatus(e.data ? 'saving' : 'saved');
          if (!e.data && pendingNavRef.current) { pendingNavRef.current = false; navigate(backPath); }
        },
      };
      loadDocsAPI(data.documentServerUrl).then(() => {
        if (!document.getElementById(containerId)) return;
        editorRef.current = new window.DocsAPI.DocEditor(containerId, config);
        setLoading(false);
      }).catch(() => { setError('Failed to load OnlyOffice editor'); setLoading(false); });
    });

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (editorRef.current) { try { editorRef.current.destroyEditor(); } catch (_) {} editorRef.current = null; }
    };
  }, [id, handleBeforeUnload, navigate, backPath]);

  if (error) return (
    <div className="min-h-screen bg-gray-100 p-6">
      <button onClick={() => navigate(backPath)} className="text-indigo-600 hover:text-indigo-800 mb-4 inline-block">&larr; Back</button>
      <div className="bg-white rounded-lg shadow p-6 text-red-600">{error}</div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col">
      <div className="bg-white shadow-sm px-4 py-2 flex items-center justify-between flex-shrink-0">
        <button onClick={handleBack} disabled={saving} className="text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50">
          {saving ? '← Saving...' : '← Back to Policies'}
        </button>
        {saveStatus === 'saving' && !saving && <span className="text-sm text-amber-600">● Unsaved changes</span>}
        {saveStatus === 'saved' && <span className="text-sm text-green-600">✓ Saved</span>}
      </div>
      {loading && <div className="flex-1 flex items-center justify-center text-gray-500">Loading editor...</div>}
      <div className="flex-1" style={{display: loading ? 'none' : 'block'}}>
        <div id={editorIdRef.current} style={{height: '100%'}} />
      </div>
    </div>
  );
}
